import { Box, Divider } from "@mui/material";
import { Color, DotLegalChip, DotLegalHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import RiskLevelChip from "../../RiskLevelChip";
import { useCurrentAssessmentCardStyles } from "./CurrentAssessmentCard.styles";
import { CurrentAssessmentCardModel } from "./AssessDialog.types";
import { assessmentConfidenceTranslationKey } from "../../AssessmentConfidence";
import LinkedControlsBox from "../../../policy/policySections/LinkedControlsBox";
import MentionInputRender from "@common/components/mentionInput/MentionInputRender";

export interface ICurrentAssessmentCard {
    model: CurrentAssessmentCardModel;
}

export function CurrentAssessmentCard({ model }: ICurrentAssessmentCard) {
    const styles = useCurrentAssessmentCardStyles();
    const { translateString } = useTranslation();

    const renderDimension = (
        dimensionLabel: string,
        level: number | null,
        levelLabel: string,
        chipsLabel: string,
        chipNames: Array<string>,
        justification: string
    ) => (
        <Box sx={styles.dimension}>
            <Box sx={styles.titleLevel}>
                <Box sx={styles.label}>{dimensionLabel}:</Box>
                <Box sx={styles.level}>{level != null ? `${level} – ${levelLabel}` : "–"}</Box>
            </Box>
            {chipNames.length > 0 && (
                <Box sx={styles.chipsGroup}>
                    <Box sx={styles.label}>{chipsLabel}:</Box>
                    <Box sx={styles.chips}>
                        {chipNames.map((name) => (
                            <DotLegalChip key={name} value={name} isAutocomplete size="small" isSelected />
                        ))}
                    </Box>
                </Box>
            )}
            {justification.length > 0 && <Box sx={styles.justification}>{justification}</Box>}
        </Box>
    );

    return (
        <Box sx={styles.root}>
            <DotLegalHeader headerStyle="small" color="primary" marginBottom={0}>
                {translateString("currentAssessment")}
            </DotLegalHeader>

            <Box sx={styles.summary}>
                <Box sx={styles.summaryItem}>
                    <Box sx={styles.label}>{translateString("riskLevel")}:</Box>
                    {model.risk != null && model.riskStyle != null && (
                        <RiskLevelChip level={model.risk} label={model.riskLabel} color={model.riskStyle.color} icon={model.riskStyle.icon} />
                    )}
                </Box>

                {model.exposureConfigured && (
                    <Box sx={styles.summaryItem}>
                        <Box sx={styles.label}>{model.exposureLabel}:</Box>
                        <Box sx={styles.level}>{model.exposureValueText ?? "–"}</Box>
                    </Box>
                )}

                {model.confidence != null && (
                    <Box sx={styles.summaryItem}>
                        <Box sx={styles.label}>{translateString("assessmentConfidence")}:</Box>
                        <Box sx={styles.level}>{translateString(assessmentConfidenceTranslationKey[model.confidence])}</Box>
                    </Box>
                )}
            </Box>

            <Box sx={styles.assessedBy}>{translateString("assessedByOn", { name: model.assessedByName, date: model.assessmentDateLabel })}</Box>
            {model.participantNames.length > 0 && (
                <Box sx={styles.assessedBy}>
                    {translateString("participants")}: {model.participantNames.join(", ")}
                </Box>
            )}

            <Divider sx={styles.divider} />

            <Box sx={styles.dimensions}>
                {renderDimension(
                    translateString("consequenceLevel"),
                    model.consequence,
                    model.consequenceLabel,
                    translateString("consequences"),
                    model.consequenceTypeNames,
                    model.consequenceJustification
                )}
                {renderDimension(
                    translateString("probabilityLevel"),
                    model.probability,
                    model.probabilityLabel,
                    translateString("securityMeasures"),
                    model.measureNames,
                    model.probabilityJustification
                )}
            </Box>

            {model.reviewNote.length > 0 && (
                <>
                    <Divider sx={styles.divider} />
                    <Box sx={styles.reviewNote}>
                        <Box sx={styles.label}>{translateString("reviewNoteLabel")}:</Box>
                        <Box sx={styles.reviewNoteText}>{model.reviewNote}</Box>
                    </Box>
                </>
            )}

            {model.note.length > 0 && (
                <>
                    <Divider sx={styles.divider} />
                    <Box sx={styles.reviewNote}>
                        <Box sx={styles.label}>{translateString("note")}:</Box>
                        <Box sx={styles.reviewNoteText}>
                            <MentionInputRender value={model.note} />
                        </Box>
                    </Box>
                </>
            )}

            {/* The card is a read-only record of the assessment as it stands, so coverage is listed as plain chips
                via LinkedControlsBox rather than the editable LinkedControlsSection: no picker, no chip remove.
                Maintaining coverage happens in the dialog's own controls section on the reassess stage — where
                both boxes are visible at once, hence the heading that says which set this one is. */}
            {model.linkedControls.length > 0 && (
                <>
                    <Divider sx={styles.divider} />
                    <LinkedControlsBox controls={model.linkedControls} heading="existingControls" noPadding noBorder />
                </>
            )}
        </Box>
    );
}
